import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PaymentFlowStatus from '../../components/PaymentFlowStatus';
import { trackPaymentCancelled, trackPaymentFailed } from '../../utils/eventTracker';

const PLAN_PARAM_BY_ID = {
  pro_1month: '1month',
  pro_3month: '3month',
  pro_6month: '6month',
  pro_12month: '12month',
};

// 모바일 카드 등록창은 닫으면 실패 URL로 돌아온다. 토스가 주는 취소 코드는 둘 다 '취소'로 본다.
const CANCEL_CODES = new Set(['USER_CANCEL', 'PAY_PROCESS_CANCELED']);

const BillingFail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const errorCode = searchParams.get('code');
  const errorMessage = searchParams.get('message');
  const planId = searchParams.get('planId');
  const amount = searchParams.get('amount');
  const promo = searchParams.get('promo');
  const planParam = PLAN_PARAM_BY_ID[planId] || '3month';
  const isCancelled = CANCEL_CODES.has(errorCode);

  useEffect(() => {
    // 새로고침·뒤로가기는 같은 history 항목이라 한 번만 남긴다.
    // 다시 등록하기 → 토스에서 또 돌아온 화면은 새 항목이라 같은 주소여도 다시 남긴다.
    const historyState = window.history.state || {};
    if (historyState.billingFailTracked) return;
    try {
      window.history.replaceState({ ...historyState, billingFailTracked: true }, '');
    } catch (_) {
      // history 를 못 쓰는 환경은 중복 가능성을 감수하고 남긴다.
    }
    const planForTracking = PLAN_PARAM_BY_ID[planId] || 'unknown';
    const amountForTracking = parseInt(amount, 10) || 0;
    if (isCancelled) {
      trackPaymentCancelled(planForTracking, amountForTracking, 'billing_redirect');
    } else {
      trackPaymentFailed(planForTracking, amountForTracking, errorCode || 'UNKNOWN', errorMessage || '');
    }
  }, [planId, amount, isCancelled, errorCode, errorMessage]);

  const errorDescriptions = {
    USER_CANCEL: '카드 등록을 취소했어요. 등록할 준비가 되면 다시 진행해주세요.',
    PAY_PROCESS_CANCELED: '카드 등록을 취소했어요. 등록할 준비가 되면 다시 진행해주세요.',
    INVALID_CARD: '카드 정보를 확인하지 못했어요. 카드 번호와 유효기간을 확인해주세요.',
    EXPIRED_CARD: '유효기간이 지난 카드예요. 다른 카드를 등록해주세요.',
  };

  const handleRetry = () => {
    const params = new URLSearchParams({ plan: planParam });
    if (amount) params.set('price', amount);
    if (promo) params.set('promo', promo);
    navigate(`/subscription/pro?${params.toString()}`);
  };

  const description = errorDescriptions[errorCode]
    || '카드를 등록하지 못했어요. 잠시 후 다시 시도하거나 다른 카드를 사용해주세요.';
  const detailCode = [errorCode, errorMessage].filter(Boolean).join(' · ');

  return (
    <PaymentFlowStatus
      variant="error"
      title={isCancelled ? '카드 등록을 취소했어요' : '카드를 등록하지 못했어요'}
      description={description}
      detailCode={detailCode}
      primaryAction={{ label: '카드 다시 등록하기', onClick: handleRetry }}
      secondaryAction={{
        label: '구독 관리로 돌아가기',
        onClick: () => navigate('/subscription'),
      }}
      footer={(
        <p>
          같은 문제가 계속되면{' '}
          <a href="mailto:angel@plactical.com">고객센터로 알려주세요</a>
        </p>
      )}
    />
  );
};

export default BillingFail;
